import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Label, Textarea } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { brokerFeeSettlementsQueryKey, useBrokerFeeSettlements } from '@/hooks/queries/brokers/use-broker-fee-settlements';
import { updateFeeSettlementState } from '@/services/api/broker-fee-settlements';
import type { FeeSettlementAlert } from '@/services/api/compliance-alerts';

const REJECT_REASON_MAX_LENGTH = 1000;

interface BrokerFeeSettlementReviewActionsProps {
  alert: FeeSettlementAlert;
  onClose: () => void;
  /** Dismisses the alert with the same justification (second, non-atomic call) */
  onDismiss: (justification: string) => Promise<void>;
}

/**
 * Review footer for broker_fee_settlement_review AND broker_fee_settlement_failed
 * alerts. Approving initiates a REAL payout via fast-funds/Kinexys; a FAILED
 * settlement supports the same PAY action as a retry.
 */
export const BrokerFeeSettlementReviewActions = ({ alert, onClose, onDismiss }: BrokerFeeSettlementReviewActionsProps) => {
  const queryClient = useQueryClient();
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // The outcome call and the dismiss are two non-atomic requests. If the dismiss
  // fails and the operator retries in the confirm dialog, the (already successful)
  // payment-initiating outcome call must not fire a second time.
  const isOutcomeApplied = useRef(false);

  const isRetry = alert.event_type === 'broker_fee_settlement_failed';
  const approveLabel = isRetry ? 'Retry Payment' : 'Approve & Pay';

  // The alert payload is free-form Dynamo data — guard the fields this flow depends on
  // before letting them reach a payment-initiating mutation.
  const isAlertValid = typeof alert.broker_name === 'string' && alert.broker_name.length > 0 && typeof alert.settlement_id === 'string' && alert.settlement_id.length > 0;

  // Server-driven gating: only Ops can review in production. While the query is in
  // flight (or failed) the buttons stay enabled — the state endpoint enforces 403 anyway.
  const capabilities = useBrokerFeeSettlements(alert.pid).data?.capabilities;
  const isReviewDenied = capabilities?.canReview === false;

  const amountLabel = alert.payable_amount ? `${alert.payable_amount} ${alert.currency ?? 'USD'}` : 'the calculated amount';

  const applyOutcome = async (action: 'PAY' | 'REJECT', justification: string, rejection_reason?: string) => {
    if (!isOutcomeApplied.current) {
      await updateFeeSettlementState({
        broker_account_id: alert.pid,
        settlement_id: alert.settlement_id,
        action,
        ...(rejection_reason ? { rejection_reason } : {}),
        comment: justification,
      });
      isOutcomeApplied.current = true;
    }
    // Outside the at-most-once block so a dismiss retry still refreshes the tab
    await queryClient.invalidateQueries({ queryKey: brokerFeeSettlementsQueryKey(alert.pid) });
    await onDismiss(justification);
  };

  const handleApprove = () => {
    isOutcomeApplied.current = false;
    return confirmAction({
      title: isRetry ? 'Retry Fee Settlement Payment' : 'Approve Fee Settlement',
      description: `Proceeding with this action will initiate the payment of ${amountLabel} to ${alert.broker_name} via Kinexys and dismiss the alert with ID ${alert.alert_id}. Your action here will be logged and will be auditable.`,
      actionLabel: approveLabel,
      onConfirm: (justification) => applyOutcome('PAY', justification),
    });
  };

  const handleRejectContinue = async () => {
    const reason = rejectReason.trim();
    if (!reason) return;
    setIsRejectDialogOpen(false);
    isOutcomeApplied.current = false;
    await confirmAction({
      title: 'Reject Fee Settlement',
      description: `Proceeding with this action will reject the fee settlement for ${alert.broker_name} without payment and dismiss the alert with ID ${alert.alert_id}. Your action here will be logged and will be auditable.`,
      actionLabel: 'Reject Fee Settlement',
      destructive: true,
      onConfirm: (justification) => applyOutcome('REJECT', justification, reason),
    });
  };

  return (
    <div className="flex flex-col items-end gap-2">
      {isAlertValid ? null : <p className="text-sm text-red-500">This alert is missing its broker name or settlement id — review actions are disabled.</p>}
      {isReviewDenied ? <p className="text-sm text-muted-foreground">Fee settlement review is restricted to Operations — actions are disabled.</p> : null}
      <div className="flex justify-end gap-2">
        <Button onClick={onClose} type="button" variant="outline">
          Close
        </Button>
        <Button disabled={!isAlertValid || isReviewDenied} onClick={handleApprove} type="button" variant="primary">
          {approveLabel}
        </Button>
        <Button disabled={!isAlertValid || isReviewDenied} onClick={() => setIsRejectDialogOpen(true)} type="button" variant="destructive">
          Reject Settlement
        </Button>
      </div>

      <Dialog onOpenChange={setIsRejectDialogOpen} open={isRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Fee Settlement</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label>Reject reason</Label>
            <Textarea
              maxLength={REJECT_REASON_MAX_LENGTH}
              onChange={(event) => setRejectReason(event.target.value)}
              placeholder="Why is this settlement not being paid?"
              value={rejectReason}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setIsRejectDialogOpen(false)} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={!rejectReason.trim()} onClick={handleRejectContinue} type="button" variant="destructive">
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
BrokerFeeSettlementReviewActions.displayName = 'BrokerFeeSettlementReviewActions';
